import React, { useId, useRef, useState } from 'react';
import { ArrowDown, X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Eyebrow } from './SectionBand';
import { DeviceFrame } from './DeviceFrame';
import { proofAssets } from '../../lib/proofAssets';
import { useModalFocus } from '../../lib/useModalFocus';
const readerContext = ['Known reader interest', 'What matters to them', 'Where they are stuck', 'Actions they have taken', 'What kind of support may fit'];
const authorEnablement = ['Stronger sales conversations', 'Speaker Slides and CTAs', 'Workbook and Working Plans', 'Podcast Pitch and Prep', 'Email Follow-Up and Nurture', 'LinkedIn Posts and Paths', 'Clearer offer paths'];
const CONSOLE_SRC = proofAssets.authorConsole;
const CONSOLE_LABEL = 'Simplify to Amplify · Author console';
const CONSOLE_FRAME = 'Reader activity becomes author-side context you can actually use.';
function ConsoleLightbox({
  onClose
}: {
  onClose: () => void;
}) {
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  useModalFocus(rootRef, onClose);
  return <div ref={rootRef} tabIndex={-1} className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8 focus:outline-none" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-593" data-mp="1e5ulvsk1w7uc">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-594" data-mp="146lkiemuvqxy">
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-200/80 bg-paper px-5 py-5 md:px-6 md:py-6" data-id="element-595" data-mp="uhlguhxes7df">
          <div className="min-w-0 max-w-3xl pr-2" data-id="element-596" data-mp="qdw02emz6u2y">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-597" data-mp="ligl1jd8es4y">
              Start the next conversation with context
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-598" data-mp="bihcvnkq53sm">
              {CONSOLE_FRAME}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-599" data-mp="1xq4t3910rq9">
            <X className="h-5 w-5" aria-hidden="true" data-id="element-600" data-mp="3t3ra0v6b8r3z" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-paper px-3 py-3 md:px-5 md:py-5" data-id="element-601" data-mp="kmmzv9c8zo9j">
          <img src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" className="mx-auto block h-auto w-full max-w-full rounded-lg" data-id="element-602" data-mp="pn2a3s18gfna" />
        </div>
      </div>
    </div>;
}
export function AuthorLeverage() {
  const [enlarged, setEnlarged] = useState(false);
  return <section className="overflow-x-clip bg-navy px-6 py-20 text-paper md:px-10 md:py-28" data-id="element-603" data-mp="13jfub8x5iv5g">
      <div className="mx-auto max-w-6xl" data-id="element-604" data-mp="18d0loi48oqvo">
        <div className="max-w-3xl" data-id="element-605" data-mp="2t1l2t0mrx2q3">
          <Reveal data-id="element-606" data-mp="2t0jrqw5rcg8d">
            <Eyebrow data-id="element-607" data-mp="2t01mxb514tou">Start the next conversation with context</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-608" data-mp="2o1qdv2mk6n7o">
              Know what mattered before the conversation starts.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-609" data-mp="2xsundmtgtfv4">
              The author can see the reader’s pattern, saved move, support
              interest, and activity—so follow-up is more relevant and the right
              offer is easier to identify.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid min-w-0 items-start gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14" data-id="element-610" data-mp="2k1g4dfzk5eo2">
          <Reveal delay={0.1} className="min-w-0" data-id="element-611" data-mp="2f1tqr5fthweq">
            <button type="button" onClick={() => setEnlarged(true)} className="group w-full max-w-full min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-0" aria-label="Enlarge: Author console" data-id="element-612" data-mp="2f4g1tf1x2u1p">
              <span className="block transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_28px_50px_-28px_rgba(0,0,0,0.55)]" data-id="element-613" data-mp="201g8ihkmipnh">
                <DeviceFrame variant="browser" src={CONSOLE_SRC} alt="Author console — reader activity becomes author-side context" chromeLabel={CONSOLE_LABEL} aspect="16 / 10" data-id="element-614" data-mp="2912z08bteb1r" />
              </span>
              <span className="mt-3 block text-center text-sm font-medium text-paper/55 transition-colors group-hover:text-paper/80" data-id="element-615" data-mp="2iz8zqmlvxb51">
                Click to enlarge
              </span>
            </button>
          </Reveal>

          <Reveal delay={0.15} data-id="element-616" data-mp="2sok8u98rufsx">
            <div data-id="element-617" data-mp="2splt94bogenb">
              <div className="rounded-2xl border border-white/12 bg-white/[0.04] p-6" data-id="element-618" data-mp="2sr6da00d8f6x">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass-light" data-id="element-619" data-mp="2wsi58c6ppcia">
                  Reader context
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-620" data-mp="36nsar9wbk20t">
                  {readerContext.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-621" data-mp="3gt1n8bf1ms91">
                      {item}
                    </span>)}
                </div>
              </div>

              <div className="flex flex-col items-center py-3" data-id="element-622" data-mp="36v41buknp7jl">
                <ArrowDown className="h-5 w-5 text-seaglass-light" data-id="element-623" data-mp="32t8wwh7fie1l" />
                <span className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-paper/45" data-id="element-624" data-mp="32v3lq6ltmqay">
                  which becomes
                </span>
              </div>

              <div className="rounded-2xl border-2 border-seaglass/50 bg-white/[0.06] p-6 shadow-[0_24px_50px_-30px_rgba(0,0,0,0.6)]" data-id="element-625" data-mp="3ulzy763yysxp">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sand" data-id="element-626" data-mp="3lrv32ahsyvcd">
                  Author enablement
                </p>
                <div className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-627" data-mp="m5oaizlftity">
                  {authorEnablement.map(item => <div key={item} className="flex items-start gap-2.5 text-paper/90" data-id="element-628" data-mp="hc3ng2ldu99j">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sand" data-id="element-629" data-mp="2blqwih948fem" />
                      <span className="leading-snug" data-id="element-630" data-mp="2gjsf712mqcpk">{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mx-auto mt-12 max-w-2xl text-center text-base text-paper/55" data-id="element-631" data-mp="26tgzyidhwh3d">
          <span className="block" data-id="element-632" data-mp="1su7sc73cuvb0">
            Client experience shown to demonstrate the reader and author path.
          </span>
          <span className="mt-1 block" data-id="element-633" data-mp="1xvf3mg4picmf">
            No performance outcomes are represented.
          </span>
        </p>
      </div>

      {enlarged && <ConsoleLightbox onClose={() => setEnlarged(false)} data-id="element-634" data-mp="1nvo9wgnz01ne" />}
    </section>;
}